import React, { useCallback, useEffect, useRef, useState } from 'react';
import 'regenerator-runtime/runtime';
import { ResultReason } from 'microsoft-cognitiveservices-speech-sdk';
import { selectLocalPeer, useAVToggle, useHMSStore, usePreviewJoin } from '@100mslive/react-sdk';
import { Flex, Loading } from '@100mslive/roomkit-react';
import SidePane from '../../../layouts/SidePane';
import logo from '../../../assets/images/incruiter_logo.svg';
import { defaultPreviewPreference, UserPreferencesKeys, useUserPreferences } from '../../hooks/useUserPreferences';
import { CameraMicPermission } from '../../Pages/components/CameraMicPermission';
import { NetworkConnectionLost } from '../../Pages/components/NetworkConnectionLost';
import AudioVideoPreCheck from './components/AudioVideoPreCheck';
import AcknowledgeModal from './components/AcknowledgeModal';
import { isMobileOrTabletDevice } from '../../../utils/helper';

const speechSdk = require('microsoft-cognitiveservices-speech-sdk');
const PreviewJoin = ({
    skipPreview,
    form,
    setForm,
    env,
    onJoin,
    token,
    asRole,
    setIsMount,
    isNetworkError,
    permissionsChecks,
    downlinkQuality,
    setCheckNetworkError,
    isButtonDisabled,
    microsoftKeys = [],
}) => {
    const localPeer = useHMSStore(selectLocalPeer);
    const peerId = localPeer?.id;
    const [, setPreviewPreference] = useUserPreferences(UserPreferencesKeys.PREVIEW, defaultPreviewPreference);
    const [value, setValue] = useState('');
    const [speakingPermission, setSpeakingPermission] = useState(true);
    const [name, setName] = useState('');
    const { isLocalAudioEnabled, isLocalVideoEnabled } = useAVToggle();
    const [previewError, setPreviewError] = useState(false);
    const [testValidation, setTestValidation] = useState(form?.videoAudioCheck || false);
    const [permissionChecksFailed, setPermissionChecksFailed] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [azureError, setAzureError] = useState(false);
    const [azureValue, setAzureValue] = useState([]);
    const videoContainerRef = useRef(null);

    const randomIndex = Math.floor(Math.random() * microsoftKeys.length);

    function sttFromMic() {
        const mircosoftData = microsoftKeys[randomIndex];
        if (mircosoftData) {
            const speechConfig = speechSdk.SpeechConfig.fromSubscription(
                // process.env.REACT_APP_MICROSOFT_KEY,
                // process.env.REACT_APP_MICROSOFT_REGION
                microsoftKeys[randomIndex]?.value,
                microsoftKeys[randomIndex]?.additionalValue
            );
            speechConfig.speechRecognitionLanguage = 'en-US';

            const audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();
            const recognizer = new speechSdk.SpeechRecognizer(speechConfig, audioConfig);

            const results = [];
            recognizer.startContinuousRecognitionAsync();
            recognizer.recognized = (s, e) => {
                if (e.result.reason == ResultReason.RecognizedSpeech) {
                    results.push(e.result.text);
                    setAzureValue([...results]);
                } else if (e.result.reason == ResultReason.NoMatch) {
                    setAzureError('Speech could not be recognized.');
                }
            };
        }
    }

    useEffect(() => {
        const handleStatusChange = () => {
            setIsOnline(navigator.onLine);
        };
        window.addEventListener('online', handleStatusChange);
        window.addEventListener('offline', handleStatusChange);
        return () => {
            window.removeEventListener('online', handleStatusChange);
            window.removeEventListener('offline', handleStatusChange);
        };
    }, [isOnline]);

    useEffect(() => {
        const checkAndUpdateState = () => {
            if (permissionsChecks === false) setPermissionChecksFailed(true);
        };
        const timeoutId = setTimeout(() => {
            checkAndUpdateState();
        }, 30000);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [permissionsChecks]);

    useEffect(() => {
        if (!name) setName(form?.candidateName);
    }, [form?.candidateName]);

    const startListening = () => {
        sttFromMic();
    };

    useEffect(() => {
        setValue(azureValue.join(' '));
    }, [azureValue]);

    useEffect(() => {
        if (azureError) {
            console.error('Speech recognition is not supported in this browser or on this device.');
            // Handle the lack of support, disable speech-related features, or show a message to the user.
            setSpeakingPermission(false);
        }
    }, [azureError]);

    const { preview, join } = usePreviewJoin({
        name,
        token,
        initEndpoint: env ? `https://${env}-init.100ms.live/init` : undefined,
        initialSettings: {
            isAudioMuted: skipPreview || false,
            isVideoMuted: skipPreview || false,
            speakerAutoSelectionBlacklist: ['Yeti Stereo Microphone'],
        },
        captureNetworkQualityInPreview: true,
        handleError: (_, method) => {
            if (method === 'preview') {
                setPreviewError(true);
            }
        },
        asRole,
    });
    useEffect(() => {
        if (testValidation) setIsMount(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testValidation]);
    const savePreferenceAndJoin = useCallback(() => {
        setPreviewPreference({
            name,
            isAudioMuted: !isLocalAudioEnabled,
            isVideoMuted: !isLocalVideoEnabled,
        });
        join();
        onJoin && onJoin();
    }, [join, isLocalAudioEnabled, isLocalVideoEnabled, name, setPreviewPreference, onJoin]);
    useEffect(() => {
        if (token) {
            if (skipPreview) {
                savePreferenceAndJoin();
            } else {
                preview();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, skipPreview]);
    if (isNetworkError) return <NetworkConnectionLost />;
    if (testValidation)
        return (
            <AcknowledgeModal
                form={form}
                onJoin={savePreferenceAndJoin}
                name={name}
                error={previewError}
                value={value}
                token={token}
                speakingPermission={speakingPermission}
                videoContainerRef={videoContainerRef}
                isButtonDisabled={isButtonDisabled}
            />
        );
    return (
        <Flex
            css={{
                flex: '1 1 0',
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)',
                overflowY: 'auto',
                width: '100%',
                '@md': {
                    height: '100%',
                },
            }}
            justify="center"
            align="center"
        >
            {isMobileOrTabletDevice() && (
                <img
                    src={logo}
                    alt="logo"
                    style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        width: 74,
                        height: 30.5,
                        objectFit: 'contain',
                    }}
                />
            )}
            {token ? (
                permissionChecksFailed ? (
                    <CameraMicPermission />
                ) : (
                    <AudioVideoPreCheck
                        peerId={peerId}
                        setForm={setForm}
                        isLocalAudioEnabled={isLocalAudioEnabled}
                        isLocalVideoEnabled={isLocalVideoEnabled}
                        downlinkQuality={downlinkQuality}
                        startListening={startListening}
                        setTestValidation={setTestValidation}
                        setCheckNetworkError={setCheckNetworkError}
                    />
                )
            ) : (
                <Loading size={100} />
            )}
            <SidePane
                css={{
                    position: 'unset',
                    mr: '$10',
                    '@lg': { position: 'fixed', mr: '$0' },
                }}
            />
        </Flex>
    );
};

export default PreviewJoin;
